import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useMemo } from 'react'
import FAQ, { FAQItem } from 'src/components/FAQ'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'
import {
  buildOnDemandFaqEntry,
  resolveOnDemandLandingUrl
} from 'src/utils/onDemandFaq'

type IProps = {
  pillar: PillarPage
  /** Current city slug — when provided and resolves to a covered, partner-active
   *  on-demand market, the on-demand FAQ entry links to the city-specific page.
   *  When undefined and at least one market has coverage, the entry links to
   *  the generic on-demand parent page. The entry is suppressed entirely on
   *  non-SelfStorage pillars or when no market has coverage. */
  currentCity?: string
}

const FAQS = ({ pillar, currentCity }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const ITEMS = t('FAQS.ITEMS', {
    returnObjects: true,
    cities: tStats('CITIES')
  }) as FAQItem[]

  const items: FAQItem[] = useMemo(() => {
    // Resolve the destination URL once so the locale interpolation (`{{href}}`)
    // matches what the rendered link will navigate to. Falls back to the parent
    // generic URL only as a safety net — `buildOnDemandFaqEntry` returns null
    // (and we short-circuit) before this fallback would actually surface.
    const href =
      resolveOnDemandLandingUrl(pillar, currentCity) ??
      '/renta-mini-bodegas/almacenamiento-a-domicilio'
    const entry = buildOnDemandFaqEntry(pillar, currentCity, {
      title: t('FAQ_ON_DEMAND.title'),
      description: t('FAQ_ON_DEMAND.description', { href })
    })
    if (!entry) return ITEMS
    const { parsed } = entry
    const descriptionNode = (
      <p>
        {parsed.before}
        {parsed.href && parsed.label ? (
          <Link
            href={parsed.href}
            className="text-blue-500 underline decoration-blue-500/40 decoration-1 underline-offset-[4px] hover:decoration-blue-500"
          >
            {parsed.label}
          </Link>
        ) : null}
        {parsed.after}
      </p>
    )
    return [
      ...ITEMS,
      {
        title: entry.title,
        description: entry.descriptionPlain,
        descriptionNode
      }
    ]
    // ITEMS is recomputed every render from t(); depend on the pillar/city +
    // translation function to keep the appended entry in sync without paying
    // for a deep-equal on the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillar, currentCity, t])

  return (
    <FAQ
      title={t('FAQS.TITLE')}
      faqs={items}
      image={t('FAQS.IMAGE')}
      imageAlt={t('FAQS.IMAGE_ALT')}
      whatsappMsg={t('FAQS.WHATSAPP_MSG')}
      surface="personalized"
    />
  )
}

export default React.memo(FAQS)
